import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet, ScrollView, View, Keyboard, Platform } from 'react-native';
import { BaseAdminScreen } from '@/components';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { ActionButtons } from '@/components';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

interface MobileFormScreenProps {
  screenName: keyof AdminStackParamList;
  navigation: any;
  isSubmitting?: boolean;
  submitText?: string;
  cancelText?: string;
  onSubmit: () => void;
  onCancel: () => void;
  children: React.ReactNode;
  showDelete?: boolean;
  onDelete?: () => void;
  deleteText?: string;
  hideSubmit?: boolean;
  /** Sticky content rendered above the scrollable form area (e.g. a tab bar). */
  topSlot?: React.ReactNode;
}

export const MobileFormScreen: React.FC<MobileFormScreenProps> = ({
  screenName,
  navigation,
  isSubmitting,
  submitText = 'Save',
  cancelText,
  onSubmit,
  onCancel,
  children,
  showDelete,
  onDelete,
  deleteText,
  hideSubmit,
  topSlot,
}) => {
  const styles = useStyles();
  const wrapperRef = useRef<View>(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    const isAndroid = Platform.OS === 'android';
    const showEvent = isAndroid ? 'keyboardDidShow' : 'keyboardWillShow';
    const hideEvent = isAndroid ? 'keyboardDidHide' : 'keyboardWillHide';
    const showSub = Keyboard.addListener(showEvent, (e) => {
      const keyboardTop = e.endCoordinates.screenY;
      if (wrapperRef.current) {
        wrapperRef.current.measureInWindow((_x, y, _w, h) => {
          setKeyboardHeight(Math.max(0, y + h - keyboardTop));
        });
      } else {
        setKeyboardHeight(e.endCoordinates.height);
      }
    });
    const hideSub = Keyboard.addListener(hideEvent, () => {
      setKeyboardHeight(0);
    });
    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  return (
    <BaseAdminScreen
      screenName={screenName}
      navigation={navigation}
      showPlaceholder={false}
      scrollEnabled={false}
      contentContainerStyle={styles.baseScreenContent}
    >
      <View ref={wrapperRef} style={styles.container}>
        <View style={styles.container}>
          {topSlot}
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {children}
          </ScrollView>

          <ActionButtons
            isLoading={!!isSubmitting}
            onCancel={onCancel}
            onSubmit={onSubmit}
            submitText={submitText}
            cancelText={cancelText}
            showDelete={showDelete}
            onDelete={onDelete}
            deleteText={deleteText}
            hideSubmit={hideSubmit}
            containerStyle={styles.actionButtonsFlow}
          />
        </View>
        {keyboardHeight > 0 && <View style={{ height: keyboardHeight }} />}
      </View>
    </BaseAdminScreen>
  );
};

const useStyles = () =>
  useThemedStyles(({ components }) => {
    const admin = components.admin;
    return StyleSheet.create({
      baseScreenContent: {
        paddingHorizontal: 0,
        paddingTop: 0,
        paddingBottom: 0,
        flexGrow: 1,
        // Transparent so the gradient from BaseAdminScreen shows through; the
        // form chrome (header divider) is preserved via the themed border.
        backgroundColor: 'transparent',
        borderTopWidth: 1,
        borderTopColor: admin.dataTable.border,
      },
      container: {
        flex: 1,
      },
      scrollView: {
        flex: 1,
      },
      scrollContent: {
        padding: 16,
        paddingBottom: 16,
      },
      actionButtonsFlow: {
        position: 'relative' as const,
      },
    });
  });
